import React, { useState, useRef, useEffect } from 'react';
import { TRANSLATIONS } from './translations';
import { WORKSHOPS } from './workshops';

function App() {
  const [lang, setLang] = useState('vi');
  const [selectedWorkshops, setSelectedWorkshops] = useState({});
  const [formData, setFormData] = useState({ email: '', mobile: '', address: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeFormData, setSubscribeFormData] = useState({ email: '', mobile: '' });
  const [isSubmittingSubscribe, setIsSubmittingSubscribe] = useState(false);
  const [modalInfo, setModalInfo] = useState({ visible: false, title: '', message: '' });
  const checkoutRef = useRef(null);

  useEffect(() => {
    const { pathname } = window.location;
    if (pathname === '/success' || pathname === '/cancel') {
      window.location.replace('/');
    }
  }, []);

  const t = TRANSLATIONS[lang];

  const showModal = (message, title = '') => {
    setModalInfo({ visible: true, title, message });
  };

  const closeModal = () => {
    setModalInfo(prev => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    if (isCheckingOut && checkoutRef.current) {
      checkoutRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isCheckingOut]);

  useEffect(() => {
    const pingBackend = async () => {
      try {
        fetch('https://genxworkshop.onrender.com/ping');
      } catch (error) {
        console.error('Backend ping failed:', error);
      }
    };

    pingBackend();
  }, []);

  const toggleWorkshop = (id) => {
    setSelectedWorkshops(prev => {
      const updated = { ...prev };
      if (updated[id]) {
        delete updated[id];
      } else {
        updated[id] = 1;
      }
      return updated;
    });
  };

  const updateQuantity = (id, quantity) => {
    if (quantity < 1) return;
    setSelectedWorkshops(prev => ({
      ...prev,
      [id]: quantity
    }));
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email' && isEmailVerified) {
      setIsEmailVerified(false);
      setShowVerificationInput(false);
    }
  };

  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };

  const handleSendCode = async () => {
    if (!isValidEmail(formData.email)) {
      showModal(t.invalidEmail);
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await fetch('https://genxworkshop.onrender.com/send-emailcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, lang })
      });

      if (response.ok) {
        showModal(t.sendCodeAlert);
        setShowVerificationInput(true);
      } else {
        const errorData = await response.json();
        showModal(t.errorPrefix + (errorData.error || 'Server error'));
      }
    } catch (error) {
      console.error('Send email code failed:', error);
      showModal(t.errorPrefix + 'Server error');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    setIsVerifyingCode(true);
    try {
      const response = await fetch('https://genxworkshop.onrender.com/verify-emailcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verificationCode, email: formData.email })
      });

      if (response.ok) {
        showModal(t.verifySuccess);
        setIsEmailVerified(true);
        setShowVerificationInput(false);
      } else {
        showModal(t.verifyFailure);
      }

    } catch (error) {
      console.error('Verify email code failed:', error);
      showModal(t.errorPrefix + 'Server error');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEmailVerified) {
      showModal(t.emailNotVerified);
      return;
    }

    setIsSubmitting(true);

    const workshops = Object.entries(selectedWorkshops).map(([wsId, quantity]) => {
      const workshop = WORKSHOPS.find(ws => ws.id === parseInt(wsId));
      return {
        id: wsId,
        quantity,
        price: workshop.price,
        title: workshop[`${lang}Title`]
      };
    })

    try {
      const response = await fetch('https://genxworkshop.onrender.com/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, lang, workshops, total: calculateTotal() })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showModal(t.errorPrefix + data.error);
      }
    } catch (error) {
      console.error("Checkout failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return Object.entries(selectedWorkshops).reduce((total, [wsId, quantity]) => {
      const workshop = WORKSHOPS.find(ws => ws.id === parseInt(wsId));
      return total + (workshop ? workshop.price * quantity : 0);
    }, 0);
  };

  const handleSubscribeChange = (e) => {
    const { name, value } = e.target;
    setSubscribeFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubscribeSubmit = async (e) => {
    e.preventDefault();

    if (!subscribeFormData.email) {
      showModal('Please enter an email address');
      return;
    }

    if (!isValidEmail(subscribeFormData.email)) {
      showModal(t.invalidEmail);
      return;
    }

    setIsSubmittingSubscribe(true);
    try {
      const response = await fetch('https://genxworkshop.onrender.com/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: subscribeFormData.email, 
          mobile: subscribeFormData.mobile,
          lang
        })
      });

      if (response.ok) {
        showModal(lang === 'en' ? 'Thank you for subscribing!' : 'Cảm ơn bạn đã đăng ký!');
        setSubscribeFormData({ email: '', mobile: '' });
      } else {
        const errorData = await response.json();
        showModal(t.errorPrefix + (errorData.error || 'Server error'));
      }
    } catch (error) {
      console.error('Subscribe failed:', error);
      showModal(t.errorPrefix + 'Server error');
    } finally {
      setIsSubmittingSubscribe(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-800 text-yellow-500 font-sans">

      {/* Banner Section */}
      <header className="relative overflow-hidden py-12 md:py-20 px-4 text-center bg-yellow-400">
        {/* <img src="/banner.jpg" alt="Banner" className="h-full w-full object-cover object-center" /> */}
        {/* <div className="absolute inset-0 bg-yellow-400/80"></div> */}
        <h1 className="text-3xl md:text-9xl font-bold text-slate-900 font-serif">GenX Workshop</h1>
        <h2 className="text-xl md:text-7xl font-bold text-slate-900 font-serif">Spark Your Creativity</h2>
        <div className="absolute top-3 right-3 md:top-6 md:right-6 z-10">
          <button 
            onClick={() => setLang(lang === 'en' ? 'vi' : 'en')}
            className="bg-yellow-50 text-yellow-950 px-4 py-2 rounded-md font-bold text-sm hover:bg-yellow-100 transition-colors shadow-md"
          >
            {lang === 'en' ? 'Tiếng Việt 🇻🇳' : 'English 🇺🇸'}
          </button>
        </div>
      </header>

      {/* Alert Section */}
      <main className="max-w-6xl mx-auto px-4 py-8 md:p-8">
        {modalInfo.visible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-400/40 p-4">
            <div className="w-full max-w-md rounded-3xl bg-slate-900 p-5 shadow-2xl border border-yellow-500">
              {modalInfo.title && <h3 className="text-lg font-semibold text-white mb-3">{modalInfo.title}</h3>}
              <p className="text-sm font-semibold text-white">{modalInfo.message}</p>
              <div className="mt-6 text-right">
                <button
                  type="button"
                  onClick={closeModal}
                  className="inline-flex items-center justify-center rounded-full bg-yellow-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-yellow-600"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Intro Text */}
        {/* <section className="my-12 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-yellow-300 font-serif">{t.selectWorkshops}</h2>
        </section> */}

        {/* Workshop Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-4">
          {WORKSHOPS.map((ws) => (
            <div key={ws.id} className={`border border-slate-500 rounded-lg overflow-hidden bg-slate-900 shadow-sm transition-all ${selectedWorkshops[ws.id] ? 'ring-4 ring-yellow-400' : ''}`}>
              <img src={ws.thumbnail} alt={ws.id} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-lg text-yellow-500">{ws[`${lang}Title`]}</h3>
                <p className="text-yellow-50 text-sm mt-2">{ws[`${lang}Description`]}</p>
                <div className="mt-4 flex items-center justify-between">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 accent-yellow-400 checked:bg-yellow-400 checked:border-yellow-400"
                      checked={!!selectedWorkshops[ws.id]}
                      onChange={() => toggleWorkshop(ws.id)}
                    />
                  </label>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-semibold text-cyan-400">{ws.price}.000 VND</span>
                    <span className="text-sm font-semibold text-cyan-400">{selectedWorkshops[ws.id] > 1 ? `x ${selectedWorkshops[ws.id]}` : ''}</span>
                  </div>
                </div>
                {selectedWorkshops[ws.id] && (
                  <div className="mt-4 flex items-center justify-between bg-slate-800 p-3 rounded">
                    <span className="text-sm font-semibold text-stone-400">{t.quantityLabel}:</span>
                    <button
                      onClick={() => updateQuantity(ws.id, selectedWorkshops[ws.id] - 1)}
                      className="bg-zinc-600 hover:bg-zinc-700 text-white w-12 h-8 rounded flex items-center justify-center font-bold"
                    >
                      −
                    </button>
                    <span className="text-yellow-400 font-bold text-base">{selectedWorkshops[ws.id]}</span>
                    <button
                      onClick={() => updateQuantity(ws.id, selectedWorkshops[ws.id] + 1)}
                      className="bg-green-600 hover:bg-green-700 text-white w-12 h-8 rounded flex items-center justify-center font-bold"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Selection Confirm */}
        <div className="mt-12 mb-12 text-center">
          <button 
            onClick={() => {
              if (Object.keys(selectedWorkshops).length === 0) {
                showModal(t.alertSelect);
                return;
              }
              console.log(selectedWorkshops);
              setIsCheckingOut(true);
            }}
            className="w-full md:w-auto bg-yellow-500 text-slate-950 px-8 md:px-10 py-4 rounded-full font-bold text-lg md:text-xl hover:bg-yellow-600 transition-colors"
          >
            {t.confirmBtn} ({Object.values(selectedWorkshops).reduce((sum, qty) => sum + qty, 0)})
          </button>
        </div>

        {/* Subscribe Form */}
        {!isCheckingOut && (
          <section className="mt-12 bg-slate-900 p-4 md:p-8 rounded-xl shadow-lg border border-slate-700">
            <h1 className="text-xl md:text-2xl font-bold mb-4 text-center text-yellow-500">{t.subscribeHeading}</h1>
            <p className="mb-6 text-sm text-yellow-50 text-center">{t.subscribeDescription}</p>
            <form onSubmit={handleSubscribeSubmit} className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block text-sm font-medium mb-1 text-yellow-500">{t.emailLabel}</label>
                <input 
                  type="email" 
                  name="email" 
                  value={subscribeFormData.email} 
                  onChange={handleSubscribeChange} 
                  required
                  className="w-full p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" 
                  placeholder={t.placeholderEmail} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-yellow-500">{t.mobileLabel}</label>
                <input 
                  type="tel" 
                  name="mobile" 
                  value={subscribeFormData.mobile} 
                  onChange={handleSubscribeChange}
                  className="w-full p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" 
                  placeholder={t.placeholderMobile} 
                />
              </div>
              <button 
                type="submit" 
                disabled={isSubmittingSubscribe} 
                className="w-full bg-yellow-500 text-slate-950 py-3 rounded font-bold hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {isSubmittingSubscribe ? (
                  <>
                    <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  </>
                ) : (
                  t.subscribeBtn
                )}
              </button>
            </form>
          </section>
        )}

        {/* Contact Info Section */}
        {isCheckingOut && (
          <>
            {/* Order Summary Section */}
            <section ref={checkoutRef} className="mt-12 md:mt-16 bg-slate-900 p-4 md:p-8 rounded-xl shadow-lg border border-slate-700 mb-8">
              <h2 className="text-xl md:text-2xl font-bold mb-6 text-center text-yellow-500">{t.orderSummary}</h2>
              
              {/* Mobile View - Cards */}
              <div className="md:hidden space-y-3">
                {Object.entries(selectedWorkshops).map(([wsId, quantity]) => {
                  const workshop = WORKSHOPS.find(ws => ws.id === parseInt(wsId));
                  const subtotal = workshop ? workshop.price * quantity : 0;
                  return (
                    <div key={wsId} className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                      <h3 className="font-semibold text-yellow-50 mb-2">{workshop[`${lang}Title`]}</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">{t.orderPrice}:</span>
                          <span className="text-cyan-400">{workshop?.price}.000 VND</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">{t.quantityLabel}:</span>
                          <span className="text-cyan-400 font-semibold">{quantity}</span>
                        </div>
                        <div className="border-t border-slate-600 pt-2 mt-2 text-right">
                          <h4 className="text-yellow-400 font-semibold">{subtotal}.000 VND</h4>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="bg-slate-800 p-4 rounded-lg border-2 border-yellow-400 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg text-yellow-500">{t.orderTotal}:</span>
                    <span className="font-bold text-xl text-yellow-400">
                      {calculateTotal()}.000 VND
                    </span>
                  </div>
                </div>
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm md:text-base">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-3 px-2 text-gray-400">{t.orderWorkshops}</th>
                      <th className="text-right py-3 px-2 text-gray-400">{t.orderPrice}</th>
                      <th className="text-center py-3 px-2 text-gray-400">{t.quantityLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedWorkshops).map(([wsId, quantity]) => {
                      const workshop = WORKSHOPS.find(ws => ws.id === parseInt(wsId));
                      const subtotal = workshop ? workshop.price * quantity : 0;
                      return (
                        <tr key={wsId} className="border-b border-slate-700 hover:bg-slate-800">
                          <td className="py-3 px-2 text-yellow-50">{workshop[`${lang}Title`]}</td>
                          <td className="text-right py-3 px-2 text-cyan-400">{workshop?.price}.000 VND</td>
                          <td className="text-center py-3 px-2 text-cyan-400 font-semibold">{quantity}</td>
                          <td className="text-right py-3 px-2 text-cyan-400 font-semibold">{subtotal}.000 VND</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2">
                      <td colSpan="3" className="py-4 px-2 text-right font-bold text-lg text-yellow-500">{t.orderTotal}:</td>
                      <td className="text-right py-4 px-2 font-bold text-lg text-yellow-400">
                        {calculateTotal()}.000 VND
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <section className="mt-12 md:mt-16 bg-slate-900 p-4 md:p-8 rounded-xl shadow-lg border border-slate-700">
            <h2 className="text-xl md:text-2xl font-bold mb-6 text-center text-yellow-500">{t.contactInfo}</h2>
            <p className="mt-2 mb-6 text-sm text-yellow-50 text-center">{t.emailDescription}</p>
            <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block text-sm font-medium mb-1">{t.emailLabel}</label>
                <div className="flex flex-col md:flex-row gap-3 items-stretch">
                  <div className="flex-1 relative">
                    <input required type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" placeholder={t.placeholderEmail} />
                    {isEmailVerified && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-700 text-2xl font-bold">✓</span>
                    )}
                  </div>
                  {/* Send Code Section */}
                  {!isEmailVerified && (
                    <button type="button" onClick={handleSendCode} disabled={isSendingCode} className="bg-green-500 text-slate-950 px-4 py-3 rounded font-semibold hover:bg-green-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {isSendingCode ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        </>
                      ) : (
                        t.sendCodeBtn
                      )}
                    </button>
                  )}
                </div>
              </div>
              {/* Verify Code Section */}
              {showVerificationInput && !isEmailVerified && (
                <div>
                  <label className="block text-sm font-medium mb-1">{t.verifyCodeLabel}</label>
                  <div className="flex flex-col md:flex-row gap-3 items-stretch">
                    <input type="text" name="verificationCode" value={verificationCode} onChange={handleVerificationCodeChange} className="flex-1 p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" placeholder={t.verificationPlaceholder} />
                    <button type="button" onClick={handleVerifyCode} disabled={isVerifyingCode} className="bg-blue-600 text-slate-950 px-4 py-3 rounded font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {isVerifyingCode ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        </>
                      ) : (
                        t.verifyBtn
                      )}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1 text-yellow-500">{t.mobileLabel}</label>
                <input type="tel" name="mobile" value={formData.mobile} onChange={handleInputChange} className="w-full p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" placeholder={t.placeholderMobile} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-yellow-500">{t.addressLabel}</label>
                <textarea name="address" value={formData.address} onChange={handleInputChange} className="w-full p-3 border border-slate-700 bg-slate-950 text-white rounded focus:ring-2 focus:ring-amber-400 outline-none" rows="3" placeholder={t.placeholderAddress}></textarea>
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-yellow-500 text-slate-950 py-3 rounded font-bold hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  </>
                ) : (
                  t.checkoutBtn
                )}
              </button>
              <button type="button" onClick={() => setIsCheckingOut(false)} className="w-full text-gray-100 text-sm font-bold">{t.cancelBtn}</button>
            </form>
          </section>
          </>
        )}
      </main>
      <footer className="border-t border-slate-800 bg-slate-950 py-8">
          <p className="text-xs md:text-sm font-semibold text-gray-400 text-center mx-10">{t.footerContactInfo}</p>
      </footer>
    </div>
  );
}

export default App;
